import { createContext, useContext, useMemo, type ReactNode } from "react";
import useSWR from "swr";
import { fetcher } from "@/lib/fetcher";

interface HolidaysContextValue {
  holidays: Set<string>;
  loading: boolean;
  unavailable?: boolean;
}

const HolidaysContext = createContext<HolidaysContextValue>({
  holidays: new Set(),
  loading: true,
  unavailable: true,
});

export function HolidaysProvider({ children }: { children: ReactNode }) {
  const { data, isLoading } = useSWR<{ holidays: string[] }>(
    "/api/v1/holidays",
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 3_600_000, // 1 hour
    },
  );

  const value = useMemo<HolidaysContextValue>(
    () => ({
      holidays: new Set(data?.holidays ?? []),
      loading: isLoading,
      unavailable: !data,
    }),
    [data, isLoading],
  );

  return <HolidaysContext value={value}>{children}</HolidaysContext>;
}

export function useHolidays(): HolidaysContextValue {
  return useContext(HolidaysContext);
}
